import { getAttachmentUrl } from '@/components/Comment/AttachmentList/AttachmentList'
import { useGetErrorIssuesQuery } from '@/graph/generated/hooks'
import type { GetErrorGroupQuery } from '@/graph/generated/operations'
import {
	ExternalAttachment,
	IntegrationType,
} from '@/graph/generated/schemas'
import { copyToClipboard } from '@/util/string'
import {
	Box,
	IconSolidClickUp,
	IconSolidClipboardCopy,
	IconSolidExternalLink,
	IconSolidGithub,
	IconSolidGitlab,
	IconSolidHeight,
	IconSolidJira,
	IconSolidLinear,
	Menu,
	Text,
} from '@highlight-run/ui/components'
import { vars } from '@highlight-run/ui/vars'

import * as style from './style.css'

type Props = {
	errorGroup: GetErrorGroupQuery['error_group']
}

const getIcon = (integrationType?: IntegrationType) => {
	switch (integrationType) {
		case IntegrationType.Linear:
			return <IconSolidLinear />
		case IntegrationType.ClickUp:
			return <IconSolidClickUp />
		case IntegrationType.Height:
			return <IconSolidHeight />
		case IntegrationType.GitHub:
			return <IconSolidGithub />
		case IntegrationType.Jira:
			return <IconSolidJira />
		case IntegrationType.GitLab:
			return <IconSolidGitlab />
	}
	return undefined
}

const ErrorIssueButton = ({ errorGroup }: Props) => {
	const { data, loading } = useGetErrorIssuesQuery({
		variables: {
			error_group_secure_id: errorGroup?.secure_id ?? '',
		},
		fetchPolicy: 'network-only',
		skip: !errorGroup?.secure_id,
	})

	const issue = data?.error_issue.find(Boolean)
	if (loading || !issue) {
		return null
	}

	const url = getAttachmentUrl(issue as ExternalAttachment)
	const title = issue.title || issue.external_id
	const Icon = getIcon(issue.integration_type)

	return (
		<Menu placement="bottom-end">
			<Menu.Button
				kind="secondary"
				size="small"
				emphasis="medium"
				iconLeft={Icon}
				cssClass={style.issueButton}
			>
				<Text lines="1">{title}</Text>
			</Menu.Button>
			<Menu.List>
				<Menu.Heading>
					<Text
						size="xxSmall"
						weight="medium"
						color="n11"
						userSelect="none"
					>
						Linked issue
					</Text>
				</Menu.Heading>
				<Menu.Item
					onClick={() => {
						if (url) {
							window.open(url, '_blank')
						}
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						gap="4"
						style={{ width: '100%' }}
					>
						<IconSolidExternalLink
							size={16}
							color={vars.theme.static.content.weak}
						/>
						<Box mr="auto" cssClass={style.menuOption}>
							<Text size="small" weight="medium" userSelect="none">
								View in {issue.integration_type}
							</Text>
						</Box>
					</Box>
				</Menu.Item>
				<Menu.Item
					onClick={() => {
						if (url) {
							copyToClipboard(url, {
								onCopyText: `Copied ${issue.integration_type} issue link to clipboard!`,
							})
						}
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						gap="4"
						style={{ width: '100%' }}
						color="n8"
					>
						<IconSolidClipboardCopy
							size={16}
							color={vars.theme.static.content.weak}
						/>
						<Box mr="auto" cssClass={style.menuOption}>
							<Text size="small" weight="medium" userSelect="none">
								Copy issue link
							</Text>
						</Box>
					</Box>
				</Menu.Item>
			</Menu.List>
		</Menu>
	)
}

export default ErrorIssueButton
